<template>
    <div class="bg-gray-100 overflow-hidden rounded-lg mt-3">
        <div class="px-4 py-5 sm:p-6">
            <div class="flex flex-col mt-5" v-if="rubric.subactivities.length > 0">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200 table-fixed w-full">
                            <thead>
                                <tr>
                                    <th
                                        class="hidden md:table-cell max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                    >
                                        -
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        v-show="rubric.rubric_level_zero"
                                    >
                                        0
                                    </th>
                                    <th
                                        class="max-w-xs px-2 py-1 md:px-6 md:py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        v-for="i in rubric.rubric_levels"
                                        :key="i"
                                    >
                                        {{ i }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody v-for="subactivity in rubric.subactivities" :key="subactivity.id">
                                <template v-for="(line, lineIndex) in subactivity.lines" :key="lineIndex">
                                    <tr
                                        class="text-sm leading-5 text-gray-500 table-row md:hidden"
                                        :class="line.index % 2 !== 0 ? 'bg-white' : 'bg-gray-50'"
                                    >
                                        <td :colspan="rubric.rubric_levels + (rubric.rubric_level_zero ? 1 : 0)">
                                            {{ line.name }}
                                        </td>
                                    </tr>
                                    <tr :class="line.index % 2 !== 0 ? 'bg-white' : 'bg-gray-50'">
                                        <td class="hidden md:table-cell px-6 py-4 text-sm leading-5 text-gray-500">
                                            {{ line.name }}
                                        </td>
                                        <td
                                            v-for="level in line.levels"
                                            class="px-1 py-1 text-sm leading-5 text-gray-600 border"
                                            :class="
                                                line.level === level.level
                                                    ? getLevelClasses(level, rubric.rubric_levels)
                                                    : ''
                                            "
                                            :key="`${lineIndex}_${level.level}`"
                                            v-show="level.level !== 0 || rubric.rubric_level_zero"
                                        >
                                            <div class="block md:hidden h-6 pointer-events-none" />
                                            <span class="pointer-events-none">
                                                <span
                                                    class="hidden sm:inline-block pointer-events-none"
                                                    :class="{ 'text-gray-400': line.level !== level.level }"
                                                >
                                                    {{ level.description }}
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import GradeTranslator from '@/classes/GradeTranslator.js';
import { computed, ref } from 'vue';

const props = defineProps({
    rubric: {},
    ranges: {
        required: true,
    },
});

const rubric = ref(props.rubric);

const gradeTranslator = new GradeTranslator(props.ranges);

const getLevelClasses = (level, rubricLevels) => {
    const grade = (level.level / rubricLevels) * 10;
    return gradeTranslator.scoreToPillClasses(grade);
};

const lineGrades = computed(() => {
    return rubric.value.subactivities
        .flatMap((subactivity) => subactivity.lines)
        .map((line) => {
            const grade = (line.level / rubric.value.rubric_levels) * 10;
            return {
                id: line.id,
                name: line.name,
                level: line.level,
                decimal_score: line.level === null ? line.level : Math.round(grade * 10) / 10,
            };
        });
});

const finalGrade = computed(() => {
    const total = lineGrades.value
        .filter((l) => l.level !== null)
        .map((l) => l.level)
        .reduce((a, b) => a + b, 0);

    const maxTotal = lineGrades.value.length * rubric.value.rubric_levels;
    const decimalScore = (total / maxTotal) * 10;
    return {
        is_graded: !!lineGrades.value.filter((l) => l.level !== null).length,
        total: total,
        maxTotal: maxTotal,
        decimal_score: decimalScore,
        letter: gradeTranslator.scoreToLetter(decimalScore),
        classes: gradeTranslator.scoreToPillClasses(decimalScore),
        color: gradeTranslator.scoreToColor(decimalScore),
    };
});

function toggleLevel(line, level) {
    if (line.level === level) {
        line.level = null;
    } else {
        line.level = level;
    }
}
</script>
